import { Injectable } from '@angular/core';
import { convertMarkdownToDocx, downloadDocx } from '@mohtasham/md-to-docx';

/**
 * Options for customizing DOCX export appearance and type.
 * Allows partial configuration; defaults are provided for required style properties.
 *
 * The library's own interfaces are now available, but we still define our UserDocxOptions to allow optionality in
 * how the component uses it.These are then mapped to the library's strict Options.
 */
export interface UserDocxOptions {
  /**
   * The type of document to generate.
   * - 'document': Standard document.
   * - 'report': Report-style document.
   */
  documentType?: 'document' | 'report';

  /**
   * Style options for customizing the DOCX output.
   * Only a subset is required; others are optional.
   */
  style?: {
    // These properties are required by the library's Style interface,
    // so defaults will be provided if the user doesn't specify them.
    titleSize?: number;
    headingSpacing?: number;
    paragraphSpacing?: number;
    lineSpacing?: number;

    // All other style properties are truly optional in the library's Style interface
    heading1Size?: number;
    heading2Size?: number;
    heading3Size?: number;
    heading4Size?: number;
    heading5Size?: number;
    paragraphSize?: number;
    listItemSize?: number;
    codeBlockSize?: number;
    blockquoteSize?: number;
    tocFontSize?: number;
    tocHeading1FontSize?: number;
    tocHeading2FontSize?: number;
    tocHeading3FontSize?: number;
    tocHeading4FontSize?: number;
    tocHeading5FontSize?: number;
    tocHeading1Bold?: boolean;
    tocHeading2Bold?: boolean;
    tocHeading3Bold?: boolean;
    tocHeading4Bold?: boolean;
    tocHeading5Bold?: boolean;
    tocHeading1Italic?: boolean;
    tocHeading2Italic?: boolean;
    tocHeading3Italic?: boolean;
    tocHeading4Italic?: boolean;
    tocHeading5Italic?: boolean;
    paragraphAlignment?: "LEFT" | "CENTER" | "RIGHT" | "JUSTIFIED";
    headingAlignment?: "LEFT" | "CENTER" | "RIGHT" | "JUSTIFIED";
    heading1Alignment?: "LEFT" | "CENTER" | "RIGHT" | "JUSTIFIED";
    heading2Alignment?: "LEFT" | "CENTER" | "RIGHT" | "JUSTIFIED";
    heading3Alignment?: "LEFT" | "CENTER" | "RIGHT" | "JUSTIFIED";
    heading4Alignment?: "LEFT" | "CENTER" | "RIGHT" | "JUSTIFIED";
    heading5Alignment?: "LEFT" | "CENTER" | "RIGHT" | "JUSTIFIED";
    blockquoteAlignment?: "LEFT" | "CENTER" | "RIGHT" | "JUSTIFIED";
  };
}

/**
 * Service for exporting chat sessions to DOCX format.
 *
 * Provides methods to convert Markdown chat content to Word (DOCX) files or blobs,
 * using the @mohtasham/md-to-docx library. Supports customizable export options for
 * document type and style.
 *
 * @example
 * // Convert and download a chat session as DOCX
 * exportChatSessionService.convertAndDownload(markdown, 'chat.docx', options);
 *
 * // Convert Markdown to a DOCX Blob
 * const blob = await exportChatSessionService.convertMarkdownToBlob(markdown, options);
 */
@Injectable({
  providedIn: 'root',
})
export class ExportChatSessionService {
  /**
   * Exports chat messages to a Word (DOCX) file.  It uses the library docx.
   * @deprecated This method is deprecated and will be removed in future versions.  It is replaced by convertAndDownload method.
   */
  // exportChat(messages: IMessage[], fileName: string): void {
  //   if (!messages || messages.length === 0) return;
  //
  //   const paragraphs: Paragraph[] = [];
  //
  //   messages.filter(m => !m.system).forEach(msg => {
  //     const isUser = !msg.isAssistant;
  //     const text = msg.clipboardVersion ?? msg.content ?? '';
  //     // Split the message text into lines and filter out empty lines
  //     const lines = text.split('\n').filter(line => line.trim() !== '');
  //
  //     lines.forEach(line => {
  //       // Match unordered list items (e.g., - item, * item, • item)
  //       if (/^\s*[-*•]\s+/.test(line)) {
  //         paragraphs.push(
  //           new Paragraph({
  //             // The replace method removes the list marker (dash, asterisk, or bullet) and leading whitespace from the start of the line.
  //             // Remove the unordered list marker and leading whitespace
  //             text: line.replace(/^\s*[-*•]\s+/, ""),
  //             bullet: { level: 0 },
  //             alignment: AlignmentType.LEFT,
  //             spacing: { before: 200, after: 200 },
  //             children: [
  //               new TextRun({
  //                 shading: {
  //                   fill: isUser ? "F0F9FA" : "F9F9F9",
  //                   color: "auto",
  //                 },
  //               }),
  //             ],
  //           })
  //         );
  //       // Match numbered list items (e.g., 1. item, 2. item)
  //       } else if (/^\s*\d+\.\s+/.test(line)) {
  //         paragraphs.push(
  //           new Paragraph({
  //             // The replace method removes the number, period, and leading whitespace from the start of the line.
  //             // Remove the numbered list marker and leading whitespace
  //             text: line.replace(/^\s*\d+\.\s+/, ""),
  //             numbering: { reference: "numbered-list", level: 0 },
  //             alignment: AlignmentType.LEFT,
  //             spacing: { before: 200, after: 200 },
  //             children: [
  //               new TextRun({
  //                 shading: {
  //                   fill: isUser ? "F0F9FA" : "F9F9F9",
  //                   color: "auto",
  //                 },
  //               }),
  //             ],
  //           })
  //         );
  //       } else {
  //         // Default paragraph for normal text
  //         paragraphs.push(
  //           new Paragraph({
  //             alignment: AlignmentType.LEFT,
  //             spacing: { before: 200, after: 200 },
  //             children: [
  //               new TextRun({
  //                 text: line,
  //                 shading: {
  //                   fill: isUser ? "F0F9FA" : "F9F9F9",
  //                   color: "auto",
  //                 },
  //                 break: 1,
  //               }),
  //             ],
  //           })
  //         );
  //       }
  //     });
  //   });
  //
  //   const doc = new Document({
  //     numbering: {
  //       config: [
  //         {
  //           reference: "numbered-list",
  //           levels: [
  //             {
  //               level: 0,
  //               format: "decimal",
  //               text: "%1.",
  //               alignment: AlignmentType.LEFT,
  //             },
  //           ],
  //         },
  //       ],
  //     },
  //     sections: [{ children: paragraphs }],
  //   });
  //
  //   Packer.toBlob(doc).then(blob => {
  //     saveAs(blob, `${fileName}.docx`);
  //   });
  // }

  /**
   * Converts Markdown content to a Word (DOCX) Blob and downloads it.
   * This method uses the @mohtasham/md-to-docx library to handle the conversion.
   * @param markdownContent The Markdown string to convert.
   * @param fileName The desired filename for the downloaded DOCX file. Defaults to 'document.docx'.
   * @param options Optional configuration for the DOCX conversion.
   */
  async convertAndDownload(
    markdownContent: string,
    fileName: string = 'document.docx',
    options?: UserDocxOptions // Use your defined interface
  ): Promise<void> {
    try {
      // Build the options object that strictly adheres to the library's interfaces
      const libraryOptions: any = {}; // Use 'any' or map explicitly if the library's Options was importable

      if (options?.documentType) {
        libraryOptions.documentType = options.documentType;
      }

      libraryOptions.style = {
        // Provide defaults for the REQUIRED properties if not supplied by the user
        titleSize: options?.style?.titleSize !== undefined ? options.style.titleSize : 28, // Default title size
        headingSpacing: options?.style?.headingSpacing !== undefined ? options.style.headingSpacing : 10, // Default heading spacing
        paragraphSpacing: options?.style?.paragraphSpacing !== undefined ? options.style.paragraphSpacing : 6, // Default paragraph spacing
        lineSpacing: options?.style?.lineSpacing !== undefined ? options.style.lineSpacing : 1.0, // Default line spacing (float)
      };

      // Add all other optional style properties only if they exist
      if (options?.style) {
        for (const key in options.style) {
          if (Object.prototype.hasOwnProperty.call(options.style, key)) {
            const value = (options.style as any)[key];
            if (value !== undefined) {
              // Ensure we don't overwrite the required properties we just set defaults for,
              // unless the user explicitly provided a value for them.
              // Also, check for 'number' types to ensure it's a valid number and not 'NaN'
              if (
                key !== 'titleSize' &&
                key !== 'headingSpacing' &&
                key !== 'paragraphSpacing' &&
                key !== 'lineSpacing' &&
                (typeof value === 'number' && !isNaN(value) || typeof value === 'boolean' || typeof value === 'string') // Allow number, boolean, string
              ) {
                libraryOptions.style[key] = value;
              } else if (
                (key === 'titleSize' || key === 'headingSpacing' || key === 'paragraphSpacing' || key === 'lineSpacing') &&
                (typeof value === 'number' && !isNaN(value)) // Ensure these specific required values are also valid numbers
              ) {
                libraryOptions.style[key] = value; // Update if the user provided valid values for required properties
              }
            }
          }
        }
      }

      const blob = await convertMarkdownToDocx(markdownContent, libraryOptions); // Pass the strict options
      downloadDocx(blob, fileName);
    } catch (error) {
      console.error('Error converting and downloading DOCX:', error);
      throw error; // Re-throw to allow component to handle if needed
    }
  }

  /**
   * Converts Markdown content to a Word (DOCX) Blob.
   * Uses the @mohtasham/md-to-docx library to perform the conversion.
   *
   * @param markdownContent The Markdown string to convert.
   * @param options Optional configuration for the DOCX conversion, such as document type and style.
   * @returns A Promise that resolves to a Blob containing the generated DOCX file.
   * @throws Will throw an error if the conversion fails.
   */
  async convertMarkdownToBlob(
    markdownContent: string,
    options?: UserDocxOptions
  ): Promise<Blob> {
    try {
      const libraryOptions: any = {};

      if (options?.documentType) {
        libraryOptions.documentType = options.documentType;
      }

      libraryOptions.style = {
        titleSize: options?.style?.titleSize !== undefined ? options.style.titleSize : 28,
        headingSpacing: options?.style?.headingSpacing !== undefined ? options.style.headingSpacing : 10,
        paragraphSpacing: options?.style?.paragraphSpacing !== undefined ? options.style.paragraphSpacing : 6,
        lineSpacing: options?.style?.lineSpacing !== undefined ? options.style.lineSpacing : 1.0,
      };

      if (options?.style) {
        for (const key in options.style) {
          if (Object.prototype.hasOwnProperty.call(options.style, key)) {
            const value = (options.style as any)[key];
            if (value !== undefined) {
              if (
                key !== 'titleSize' &&
                key !== 'headingSpacing' &&
                key !== 'paragraphSpacing' &&
                key !== 'lineSpacing' &&
                (typeof value === 'number' && !isNaN(value) || typeof value === 'boolean' || typeof value === 'string')
              ) {
                libraryOptions.style[key] = value;
              } else if (
                (key === 'titleSize' || key === 'headingSpacing' || key === 'paragraphSpacing' || key === 'lineSpacing') &&
                (typeof value === 'number' && !isNaN(value))
              ) {
                libraryOptions.style[key] = value;
              }
            }
          }
        }
      }

      return await convertMarkdownToDocx(markdownContent, libraryOptions);
    } catch (error) {
      console.error('Error converting Markdown to Blob:', error);
      throw error;
    }
  }
}
